import React, { useEffect } from 'react';

interface TickerGroup {
  label: string;
  tickers: string[];
}

interface TickerPillsProps {
  tickers: string[];                  // flat list (drives keyboard nav)
  active: string;
  onChange: (ticker: string) => void;
  /** Pro compare mode: pills become HTML5 drag sources carrying the
   *  ticker symbol (text/gv-ticker). The page shows a drop zone while a
   *  drag is in flight via onTickerDragStart. */
  draggableTickers?: boolean;
  onTickerDragStart?: (ticker: string) => void;
  /** Optional grouped layout. When provided, the pills render as
   *  labelled sections (e.g. an "EQUITIES" group) instead of a flat
   *  row. Pass `tickers` as the union of all groups in canonical order
   *  so arrow-key nav still cycles through everything. */
  groups?: TickerGroup[];
}

/**
 * Ticker pill strip used at the top of /app/options. Two modes:
 *
 *   1. Flat (default) — a single row of pills, the original behavior.
 *   2. Grouped — each group renders with a small uppercase label
 *      (e.g. "EQUITIES") followed by its pills, with a thin vertical
 *      divider separating multiple groups. This is the mode the
 *      Options page uses.
 *
 * Hash routing (#SPY in URL) and ←/→ arrow-key cycling work in both
 * modes — they operate over the flat `tickers` list.
 */
const TickerPills: React.FC<TickerPillsProps> = ({ tickers, active, onChange, groups, draggableTickers, onTickerDragStart }) => {
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1).toUpperCase();
      if (hash && tickers.includes(hash) && hash !== active) {
        onChange(hash);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [tickers, active, onChange]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const idx = tickers.indexOf(active);
      if (idx < 0) return;
      let next = idx;
      if (e.key === 'ArrowLeft' && idx > 0) next = idx - 1;
      if (e.key === 'ArrowRight' && idx < tickers.length - 1) next = idx + 1;
      if (next !== idx) {
        e.preventDefault();
        const nextTicker = tickers[next];
        if (nextTicker) {
          history.replaceState(null, '', `#${nextTicker}`);
          onChange(nextTicker);
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [tickers, active, onChange]);

  const handleClick = (ticker: string) => {
    history.replaceState(null, '', `#${ticker}`);
    onChange(ticker);
  };

  const renderPill = (ticker: string) => {
    const isActive = ticker === active;
    return (
      <button
        key={ticker}
        type="button"
        role="tab"
        aria-selected={isActive}
        className={`gv-pill${isActive ? ' active' : ''}`}
        onClick={() => handleClick(ticker)}
        draggable={draggableTickers || undefined}
        onDragStart={draggableTickers ? (e) => {
          e.dataTransfer.setData('text/gv-ticker', ticker);
          e.dataTransfer.effectAllowed = 'copy';
          onTickerDragStart?.(ticker);
        } : undefined}
      >
        {ticker}
      </button>
    );
  };

  // Flat mode — original behavior.
  const useGrouped = groups && groups.some(g => g.tickers.length > 0);
  if (!useGrouped) {
    return (
      <div className="gv-pill-row" role="tablist" aria-label="Ticker selector">
        {tickers.map(renderPill)}
      </div>
    );
  }

  // Grouped mode — labelled sections separated by a thin divider.
  // Skip any group that ended up empty so we never render a stray
  // label with no pills under it.
  const visibleGroups = groups!.filter(g => g.tickers.length > 0);
  return (
    <div className="gv-pill-row gv-pill-row--grouped" role="tablist" aria-label="Ticker selector">
      {visibleGroups.map((group, idx) => (
        <React.Fragment key={group.label}>
          {idx > 0 && <span className="gv-pill-group-divider" aria-hidden="true" />}
          <span className="gv-pill-group-label" aria-hidden="true">{group.label}</span>
          {group.tickers.map(renderPill)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default TickerPills;
